import React, { createContext, useContext, useState, useRef, useMemo } from 'react';
import { Form, message } from 'antd';
import dayjs from 'dayjs';
import { participantService, pbxService, cardService } from '../../services/api';

const CallCenterContext = createContext();

export const useCallCenter = () => {
    const context = useContext(CallCenterContext);
    if (!context) {
        throw new Error('useCallCenter must be used within a CallCenterProvider');
    }
    return context;
};

export const CallCenterProvider = ({ children }) => {
    // --- Estado de UI/Diseño ---
    const [activeTab, setActiveTab] = useState('1');
    const [isSelectorModalVisible, setIsSelectorModalVisible] = useState(false);
    const [isRescheduleModalVisible, setIsRescheduleModalVisible] = useState(false);
    const [isAddCardModalVisible, setIsAddCardModalVisible] = useState(false); // Movido desde Page

    // --- Estado de Agente/Llamada ---
    const [status, setStatus] = useState('offline');
    const [isInCall, setIsInCall] = useState(false);
    const [isDialing, setIsDialing] = useState(false);
    const [formSaved, setFormSaved] = useState(false);
    const [isWrappingUp, setIsWrappingUp] = useState(false); // [NUEVO] Force local wrapup

    // --- Temporizadores y Estadísticas ---
    const [callSeconds, setCallSeconds] = useState(0);
    const [stateSeconds, setStateSeconds] = useState(0);
    const [breakSeconds, setBreakSeconds] = useState(0);
    const [breakStatuses, setBreakStatuses] = useState([]);
    const [timestamps, setTimestamps] = useState({
        dailyAccumulatedBase: 0,
        currentStatusStart: null,
        activeCallStart: null
    });


    // --- Estado de Contacto y Participante ---
    const [hasContact, setHasContact] = useState(false);
    const [isLoadingContact, setIsLoadingContact] = useState(false);
    const [currentContact, setCurrentContact] = useState(null);
    const [participantCandidates, setParticipantCandidates] = useState([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false); // For recent calls
    const [recentCalls, setRecentCalls] = useState([]);

    // --- Estado de Formularios y Entradas ---
    const [form] = Form.useForm(); // Formulario principal del guion
    const [addParticipantForm] = Form.useForm();
    const [associationForm] = Form.useForm();
    const [addCardForm] = Form.useForm(); // Movido desde Page

    const [currentForm, setCurrentForm] = useState(null);
    const [selectedPhone, setSelectedPhone] = useState(null);
    const [showAddParticipant, setShowAddParticipant] = useState(false);
    const [isCreatingParticipant, setIsCreatingParticipant] = useState(false);
    const [isAssociatingParticipant, setIsAssociatingParticipant] = useState(false);
    const [isSavingParticipant, setIsSavingParticipant] = useState(false);

    // Refs to avoid stale closures in syncWithServer
    const isCreatingParticipantRef = useRef(false);
    const isAssociatingParticipantRef = useRef(false);
    const isManualContactLoadedRef = useRef(false); // To prevent sync from clearing manually loaded contacts

    // --- Campañas y Salientes ---
    const [campaigns, setCampaigns] = useState([]);
    const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(true);
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [rescheduledCalls, setRescheduledCalls] = useState([]);
    const [isLoadingRescheduled, setIsLoadingRescheduled] = useState(false);
    const [isGettingNext, setIsGettingNext] = useState(false);
    const [rescheduleDate, setRescheduleDate] = useState(null);
    const [isRescheduling, setIsRescheduling] = useState(false);
    const [rescheduleCampaignFilter, setRescheduleCampaignFilter] = useState(null);

    // --- Datos de Llamada ---
    const [currentCallId, setCurrentCallId] = useState(null);
    const [currentCall, setCurrentCall] = useState(null);

    // --- Persistencia de Gestión (Refactor) ---
    const [currentCampaignContactId, setCurrentCampaignContactId] = useState(null);
    const [hasPendingForm, setHasPendingForm] = useState(false);
    const [isRescheduled, setIsRescheduled] = useState(false);
    const [rescheduleData, setRescheduleData] = useState(null);

    // --- Metadatos/Datos Auxiliares ---
    const [availableTags, setAvailableTags] = useState([]);
    const [availableGroups, setAvailableGroups] = useState([]);
    const [monitoredCalls, setMonitoredCalls] = useState([]);
    const [monitoredTimers, setMonitoredTimers] = useState({});

    // --- CONTADORES DINÁMICOS PARA LLAMADAS MONITOREADAS ---
    React.useEffect(() => {
        if (monitoredCalls.length === 0) {
            setMonitoredTimers({});
            return;
        }

        const tick = () => {
            const now = dayjs();
            const newTimers = {};
            monitoredCalls.forEach(call => {
                const start = dayjs(call.call_start);
                const answer = call.call_answer ? dayjs(call.call_answer) : null;

                newTimers[call.id] = {
                    wait: answer ? answer.diff(start, 'second') : now.diff(start, 'second'),
                    talk: answer ? now.diff(answer, 'second') : 0
                };
            });
            setMonitoredTimers(newTimers);
        };

        tick();
        const interval = setInterval(tick, 1000);
        return () => clearInterval(interval);
    }, [monitoredCalls]);

    // --- TEMPORIZADOR LOCAL DEL AGENTE ---
    React.useEffect(() => {
        const updateTimers = () => {
            const now = dayjs();

            // 1. Tiempo en estado actual
            if (timestamps.currentStatusStart) {
                const start = dayjs(timestamps.currentStatusStart);
                const diff = now.diff(start, 'second');

                // Si es break, actualizamos breakSeconds, si no, stateSeconds (o ambos según lógica de UI)
                // La UI usa 'stateSeconds' para el tiempo acumulado diario (dailyAccumulatedBase + diff si es online?)
                // REVISIÓN: En el código original, stateSeconds solía ser el tiempo en el estado actual.
                // Pero AgentStatusBar muestra "TIEMPO ACUMULADO" vs "EN ESTADO ACTUAL".
                // Ajustemos según la lógica de AgentStatusBar:
                // - stateSeconds -> TIEMPO ACUMULADO (Daily)
                // - breakSeconds -> EN ESTADO ACTUAL (Si no es online/offline)

                // Calculamos tiempo en estado actual
                const secondsInStatus = diff > 0 ? diff : 0;
                setBreakSeconds(secondsInStatus); // Reusamos breakSeconds para "Tiempo en estado actual"
            } else {
                setBreakSeconds(0);
            }

            // 2. Tiempo acumulado diario (Base + Tiempo en sesión actual)
            // Asumimos que si estamos logueados, el tiempo corre.
            // Si stamps.dailyAccumulatedBase viene del servidor, le sumamos el tiempo desde que inició el estado actual?
            // Depende de cómo el backend manda dailyAccumulatedBase. 
            // Usualmente backend manda el total hasta el inicio del estado, y sumamos la diferencia.
            if (timestamps.dailyAccumulatedBase !== undefined) {
                // Si start existe, sumamos lo que llevamos en este estado
                let additional = 0;
                if (timestamps.currentStatusStart) {
                    const start = dayjs(timestamps.currentStatusStart);
                    additional = now.diff(start, 'second');
                    if (additional < 0) additional = 0;
                }
                setStateSeconds(timestamps.dailyAccumulatedBase + additional);
            }

            // 3. Tiempo de llamada
            if (timestamps.activeCallStart) {
                const start = dayjs(timestamps.activeCallStart);
                const diff = now.diff(start, 'second');
                setCallSeconds(diff > 0 ? diff : 0);
            } else {
                setCallSeconds(0);
            }
        };

        const interval = setInterval(updateTimers, 1000);
        updateTimers(); // Ejecutar inmediatamente
        return () => clearInterval(interval);
    }, [timestamps]);

    // --- Referencias ---
    const selectorDismissedRef = useRef(false);
    const activeCallIdRef = useRef(null);
    const isInboundRef = useRef(false);
    const isRescheduledSession = useRef(false);
    const callTimerRef = useRef(null);
    const stateTimerRef = useRef(null);
    const breakTimerRef = useRef(null);


    // --- MANEJADORES ---
    // (Mock data removed)


    // --- ESTADO DERIVADO ---
    const hasOutbound = useMemo(() => campaigns.some(c => c.type === 'outbound'), [campaigns]);

    // --- MANEJADORES ---
    const handleNextContact = async () => {
        if (status !== 'available') {
            message.error('Debe estar en estado DISPONIBLE para realizar campañas.');
            return;
        }
        if (!selectedCampaign) {
            message.error('Seleccione una campaña primero.');
            return;
        }

        // [NUEVO] Bloquear si es campaña de origen 'agent'
        if (selectedCampaign.origen_data === 'agent') {
            message.warning('Para esta campaña debe buscar y seleccionar el contacto manualmente.');
            return;
        }

        setIsLoadingContact(true);
        try {
            const response = await pbxService.getNextContact(selectedCampaign.id);
            if (response.status === 'success' && response.data) {
                const contactData = response.data;
                // Normalizar datos si es necesario (el backend debe devolver estructura correcta)
                // Asegurar array de teléfonos
                if (!Array.isArray(contactData.phones) && contactData.phone) {
                    contactData.phones = [contactData.phone];
                }

                // [NUEVO] Reset de Sesión antes de cargar nuevo contacto
                resetSessionState();

                const normalizedContact = {
                    ...contactData,
                    campaign_contact_id: contactData.id, // Map for Reschedule compatibility
                    is_campaign_contact: true,
                    campaign_id: selectedCampaign.id,
                    phones: Array.isArray(contactData.phones) ? contactData.phones : [contactData.phone].filter(Boolean)
                };

                setCurrentContact(normalizedContact);
                setSelectedPhone(normalizedContact.phones[0] || normalizedContact.phone);
                setHasContact(true);
                isManualContactLoadedRef.current = true; // Protect from sync cleanup

                // [NUEVO] Persistencia para cierre de gestión
                setCurrentCampaignContactId(normalizedContact.campaign_contact_id);
                setHasPendingForm(!!selectedCampaign.form_id);
                setIsRescheduled(false);
                setRescheduleData(null);

                // Check for candidates (Multi-match)
                if (response.data.candidates && response.data.candidates.length > 0) {
                    console.log('Candidates found for next contact:', response.data.candidates);
                    setParticipantCandidates(response.data.candidates);
                    setIsSelectorModalVisible(true);
                } else {
                    setParticipantCandidates([]);
                    setIsSelectorModalVisible(false);
                }

                message.success('Contacto cargado');
            } else {
                message.warning(response.message || 'No hay más contactos disponibles en esta campaña.');
            }
        } catch (error) {
            console.error('Error fetching next contact:', error);
            message.error('Error al obtener siguiente contacto.');
        } finally {
            setIsLoadingContact(false);
        }
    };

    const handleReleaseContact = async () => {
        if (currentContact?.campaign_contact_id) {
            try {
                await pbxService.releaseContact(currentContact.campaign_contact_id);
                if (isRescheduled) {
                    message.info("Gestión cerrada");
                } else {
                    message.info("Contacto liberado");
                }
            } catch (e) {
                console.error("Error releasing contact", e);
            }
        }
        setCurrentContact(null);
        setHasContact(false);
        isManualContactLoadedRef.current = false; // Allow sync cleanup
        setParticipantCandidates([]);
        setShowAddParticipant(false);
        setIsSelectorModalVisible(false);
    };

    // --- EFECTO DE LIMPIEZA DE FORMULARIO ---
    React.useEffect(() => {
        if (!showAddParticipant) {
            addParticipantForm.resetFields();
            associationForm.resetFields();
        }
    }, [showAddParticipant, addParticipantForm, associationForm]);

    // --- MANEJADORES DE PARTICIPANTES ---
    const fetchParticipants = async (search) => {
        if (!search || search.length < 3) return [];
        try {
            const res = await participantService.search(search);
            if (res.status === 'success' && Array.isArray(res.data)) {
                return res.data.map(p => ({
                    label: `${p.name} (${p.document_number})`,
                    value: p.id,
                    participant: p // [FIX] Pass full object for optimistic UI update
                }));
            }
            return [];
        } catch (e) {
            console.error("Participant Search Error", e);
            return [];
        }
    };

    const fetchUbigeo = async (search) => {
        if (!search || search.length < 2) return [];
        try {
            const res = await participantService.searchUbigeo(search);
            if (res.status === 'success' && Array.isArray(res.data)) {
                return res.data.map(u => ({
                    label: u.text,
                    value: u.code
                }));
            }
            return [];
        } catch (e) {
            console.error("Ubigeo Search Error", e);
            return [];
        }
    };

    const handleStartCreatingParticipant = () => {
        setIsCreatingParticipant(true);
        isCreatingParticipantRef.current = true;
        setIsSavingParticipant(false); // Reset loading state
        setShowAddParticipant(true);
        const fieldsToSet = {
            documentType: 'DNI'
        };

        if (currentCall) {
            let initialPhone = currentCall.direction === 'outbound' ? currentCall.destination : currentCall.source;
            const source = String(currentCall.source || '');
            const dest = String(currentCall.destination || '');
            if (dest.length > 6 && source.length <= 6) {
                initialPhone = dest;
            } else if (source.length > 6 && dest.length <= 6) {
                initialPhone = source;
            }
            if (initialPhone) {
                fieldsToSet.phone = initialPhone;
            }
        }

        addParticipantForm.setFieldsValue(fieldsToSet);
    };

    const handleCancelCreatingParticipant = () => {
        setIsCreatingParticipant(false);
        addParticipantForm.resetFields();

        if (currentContact) {
            setShowAddParticipant(false);
        }
        else if (participantCandidates && participantCandidates.length > 0) {
            setShowAddParticipant(false);
            setIsSelectorModalVisible(true);
        }
    };

    const handleStartAssociatingParticipant = () => {
        setIsAssociatingParticipant(true);
        isAssociatingParticipantRef.current = true;
        setIsSavingParticipant(false); // Reset loading state
        setShowAddParticipant(true);
    };

    const handleCancelAssociatingParticipant = () => {
        setIsAssociatingParticipant(false);
        isAssociatingParticipantRef.current = false;
        associationForm.resetFields();

        if (currentContact) {
            setShowAddParticipant(false);
        }
        else if (participantCandidates && participantCandidates.length > 0) {
            setShowAddParticipant(false);
            setIsSelectorModalVisible(true);
        }
    };

    const handleAssociateParticipant = async () => {
        try {
            const values = await associationForm.validateFields();
            setIsSavingParticipant(true);

            // [FIX] Robust Phone Resolution matching handleSelectParticipant
            let phoneToAssociate = currentCall?.source;
            if (currentCall) {
                phoneToAssociate = currentCall.direction === 'outbound' ? currentCall.destination : currentCall.source;
                const source = String(currentCall.source || '');
                const dest = String(currentCall.destination || '');
                if (dest.length > 6 && source.length <= 6) {
                    phoneToAssociate = dest;
                } else if (source.length > 6 && dest.length <= 6) {
                    phoneToAssociate = source;
                }
            }
            if (!phoneToAssociate && currentContact?.phones?.length > 0) {
                phoneToAssociate = currentContact.phones[0];
            }

            if (!currentCallId && !activeCallIdRef.current) {
                message.error('No hay llamada activa para asociar');
                setIsSavingParticipant(false);
                return;
            }

            const response = await participantService.associate({
                participant_id: values.participant.value,
                call_id: currentCallId || activeCallIdRef.current,
                phone: phoneToAssociate || 'unknown'
            });

            if (response.status === 'success') {
                message.success('Llamada asociada correctamente');
                setIsAssociatingParticipant(false);
                associationForm.resetFields();

                const selectedOption = values.participant;

                if (selectedOption && selectedOption.participant && selectedOption.participant.uuid) {
                    try {
                        const detailRes = await participantService.getParticipantDetail(selectedOption.participant.uuid);
                        if (detailRes && detailRes.status === 'success' && detailRes.data) {
                            const fullDetail = detailRes.data;
                            // [FIX] Nest participant data to preserve Campaign Contact metadata at root
                            setCurrentContact(prev => ({
                                ...prev,
                                participant: {
                                    ...fullDetail,
                                    phones: fullDetail.phones || [fullDetail.phone] || []
                                },
                                participant_id: fullDetail.id,
                                is_identified: true
                            }));
                            setHasContact(true);
                            setShowAddParticipant(false);
                            console.log('[handleAssociateParticipant] UI Rehydrated with full participant details', fullDetail);
                        }
                    } catch (detailError) {
                        console.error('[handleAssociateParticipant] Failed to rehydrate UI with full details', detailError);
                        // Fallback to partial if detail fetch fails
                        const p = selectedOption.participant;
                        setCurrentContact(prev => ({
                            ...prev,
                            participant: {
                                ...p,
                                phones: p.phones || [p.phone] || []
                            },
                            participant_id: p.id,
                            is_identified: true
                        }));
                        setHasContact(true);
                        setShowAddParticipant(false);
                    }
                }

                syncWithServer();
            } else {
                message.error(response.message || 'Error al asociar');
            }
        } catch (error) {
            console.error('Validate Failed:', error);
        } finally {
            setIsSavingParticipant(false);
        }
    };

    const handleSaveParticipant = () => {
        addParticipantForm.validateFields().then(async (values) => {
            try {
                // Fix: Get valid Call ID from Refs or Object if state is null
                const validCallId = currentCallId || activeCallIdRef.current || (currentCall ? currentCall.call_id : null);

                console.log('Creating participant with Call ID:', validCallId);

                const response = await participantService.create({
                    name: values.fullName,
                    homecode: values.homecode,
                    registry_date: values.registryDate ? values.registryDate.format('YYYY-MM-DD') : null,
                    gender: values.gender,
                    age: values.age,
                    nationality: values.nationality,
                    card_number: values.cardNumber,
                    document_type: values.documentType,
                    document_number: values.documentNumber,
                    address: values.address,
                    ubigeo_code: values.ubigeo ? values.ubigeo.value : null,
                    email: values.email,
                    phones: [values.phone],
                    groups: values.groups,
                    call_id: validCallId
                });

                if (response.status === 'success') {
                    message.success('Participante creado correctamente');
                    setIsCreatingParticipant(false);
                    setShowAddParticipant(false);
                    setHasContact(true);

                    const newParticipantData = {
                        uuid: response.data.uuid,
                        id: response.data.id, // Ensure ID is present if returned
                        participant_id: response.data.id, // Explicitly set participant_id
                        name: values.fullName,
                        homecode: values.homecode,
                        registry_date: values.registryDate ? values.registryDate.format('YYYY-MM-DD') : null,
                        gender: values.gender,
                        age: values.age,
                        nationality: values.nationality,
                        card_number: values.cardNumber,
                        document_type: values.documentType,
                        document_number: values.documentNumber,
                        address: values.address,
                        email: values.email,
                        phones: [values.phone],
                        cards: [],
                        is_identified: true
                    };

                    // [FIX] Nest new participant data
                    setCurrentContact(prev => ({
                        ...prev,
                        participant: newParticipantData,
                        participant_id: response.data.id,
                        is_identified: true
                    }));
                } else {
                    message.error(response.data?.message || 'Error al crear el participante');
                }
            } catch (error) {
                console.error("Error creating participant", error, values);
                message.error('Error al crear el participante');
            }
        }).catch(info => {
            console.log('Validate Failed:', info);
            message.error('Por favor complete todos los campos requeridos');
        });
    };


    const handleSelectParticipant = async (participantId) => {
        try {
            setIsSavingParticipant(true);

            // Robust Phone Selection Strategy:
            // 1. Start with direction-based logic
            let phoneToAssociate = currentCall.direction === 'outbound'
                ? currentCall.destination
                : currentCall.source;

            // 2. Fallback Heuristic: Prefer the number that looks like a valid external phone (length > 6)
            // This handles cases where direction might be mislabeled or inverted relative to the agent.
            const source = String(currentCall.source || '');
            const dest = String(currentCall.destination || '');
            const isSourceExternal = source.length > 6;
            const isDestExternal = dest.length > 6;

            if (isDestExternal && !isSourceExternal) {
                // Destination is long, Source is short (Agent) -> Use Destination
                phoneToAssociate = dest;
            } else if (isSourceExternal && !isDestExternal) {
                // Source is long, Destination is short -> Use Source
                phoneToAssociate = source;
            }

            console.log('[handleSelectParticipant] Phone Resolution:', {
                direction: currentCall.direction,
                source,
                dest,
                selected: phoneToAssociate
            });

            const response = await participantService.associate({
                participant_id: participantId,
                call_id: currentCallId,
                phone: phoneToAssociate
            });

            if (response.status === 'success') {
                message.success('Llamada asociada correctamente');
                setIsSelectorModalVisible(false);

                // [FIX] Optimistic UI Update - NEST to preserve Campaign Contact info
                const selected = participantCandidates.find(p => p.id === participantId);
                if (selected) {
                    setCurrentContact(prev => ({
                        ...prev,
                        participant: {
                            ...selected,
                            phones: selected.phones || [selected.phone] || []
                        },
                        participant_id: selected.id,
                        is_identified: true
                    }));
                    setHasContact(true);
                    setShowAddParticipant(false);
                }

                syncWithServer();
            } else {
                message.error({ content: response.message || 'Error al asociar', key: 'associating' });
            }
        } catch (error) {
            console.error("Selection error", error);
            message.error({ content: 'Error al seleccionar', key: 'associating' });
        } finally {
            setIsSavingParticipant(false);
        }
    };

    const handleCloseSelector = () => {
        setIsSelectorModalVisible(false);
        handleStartCreatingParticipant();
        selectorDismissedRef.current = true;
    };

    const handleOpenAddCardModal = () => {
        setIsAddCardModalVisible(true);
    };

    const handleCloseAddCardModal = () => {
        setIsAddCardModalVisible(false);
        addCardForm.resetFields();
    };

    const handleSaveCard = async () => {
        try {
            const values = await addCardForm.validateFields();

            if (!currentContact?.uuid) {
                message.error('No hay participante activo');
                return;
            }

            const response = await cardService.create(currentContact.uuid, {
                title: values.title,
                description: values.description,
                tags: values.tags || []
            });

            if (response.status === 'success') {
                message.success('Tarjeta agregada correctamente');
                handleCloseAddCardModal();
            } else {
                message.error(response.message || 'Error al agregar la tarjeta');
            }
        } catch (error) {
            console.error('Error creating card:', error);
            if (!error.errorFields) {
                message.error('Error al agregar la tarjeta');
            }
        }
    };

    // --- LÓGICA DE RESET DE SESIÓN (NUEVO) ---
    const resetSessionState = () => {
        console.log(" >>> RESET SESSION STATE <<< ");

        // 1. Resetear banderas de estado de gestión
        setFormSaved(false);
        setIsRescheduled(false);
        setHasPendingForm(false);
        setIsWrappingUp(false); // [NUEVO]
        setCurrentCampaignContactId(null);
        setRescheduleData(null);

        // 2. Resetear banderas de UI locales
        setShowAddParticipant(false);
        setIsCreatingParticipant(false);
        setIsAssociatingParticipant(false);
        setIsSavingParticipant(false);
        setIsSelectorModalVisible(false);

        // 3. Resetear Referencias
        isRescheduledSession.current = false;
        selectorDismissedRef.current = false;
        isManualContactLoadedRef.current = false;
        isCreatingParticipantRef.current = false;
        isAssociatingParticipantRef.current = false;

        // 4. Limpiar Formulario de Creación/Asociación
        // [FIX] SNAPSHOT HYDRATION: Only clear form if NOT in active call
        if (!isInCall && !isDialing) {
            form.resetFields(); // [CRITICAL FIX]
        } else {
            console.log("[resetSessionState] Skipping form reset - Call Active");
        }
        addParticipantForm.resetFields();
        associationForm.resetFields();
        addCardForm.resetFields();
    };

    // --- LÓGICA DE BORRADOR ---
    const DRAFT_KEY = 'stc_agent_form_draft';

    const handleFormChange = (changedValues, allValues) => {
        try {
            localStorage.setItem(DRAFT_KEY, JSON.stringify(allValues));
        } catch (e) { console.error("Error saving draft", e); }
    };

    const clearDraft = () => {
        try {
            localStorage.removeItem(DRAFT_KEY);
        } catch (e) { }
    };

    // --- SINCRONIZACIÓN CON SERVIDOR ---
    const syncWithServer = React.useCallback(async () => {
        // SALTAR SYNC SI ESTÁ MARCANDO para evitar condiciones de carrera o inestabilidad UI
        if (isDialing) return;

        try {
            const response = await pbxService.getStateSnapshot();
            if (response.status !== 'success' || !response.data) return;
            const data = response.data;
            const savedDraft = localStorage.getItem(DRAFT_KEY);

            if (data.session) {
                // Comprobar recuperación de llamada interrumpida (Handshake)
                const isInterrupted = !data.active_call && savedDraft && (data.status === 'incall' || data.status === 'busy');

                // Si el backend piensa que estamos ocupados pero no tenemos llamada + borrador -> Caída/Refresco
                if (isInterrupted) {
                    // FORCE RECOVERY STATE
                    setStatus('wrapup');
                    message.warning({
                        content: 'Recuperado de cierre inesperado. Por favor termine su gestión.',
                        duration: 10,
                    });


                    // Restaurar borrador al formulario
                    try {
                        const parsed = JSON.parse(savedDraft);
                        form.setFieldsValue(parsed);
                        setFormSaved(false);
                    } catch (e) { console.error("Auto-resume failed", e); }
                }
            }

            // NUEVO: Actualizar marcas de tiempo del servidor
            setTimestamps(prev => ({
                ...prev,
                dailyAccumulatedBase: data.session?.daily_accumulated_seconds || 0,
                currentStatusStart: data.session?.current_status_start_at || null,
                activeCallStart: data.active_call?.call_start || null
            }));

            // 2. Estado
            let backendStatus = data.status;
            if (backendStatus === 'busy') backendStatus = 'incall';

            // [NUEVO] Force Wrapup Override
            // Si el backend dice 'available' pero tenemos un formulario pendiente (isWrappingUp activado por CallCenterPage),
            // forzamos el estado visual 'wrapup' para evitar que se limpie la gestión.
            if (isWrappingUp && (backendStatus === 'available' || backendStatus === 'offline')) {
                backendStatus = 'wrapup';
            }

            if (backendStatus === 'break' && data.session.break_id) {
                setStatus(`break_${data.session.break_id}`);
            } else if (backendStatus === 'on_break' && data.session.break_id) {
                setStatus(`break_${data.session.break_id}`);
            } else if (backendStatus === 'offline') {
                setStatus('offline');
            } else {
                setStatus(backendStatus || 'online');
            }

            // 3. Estado de la llamada
            let activeCallData = data.active_call;

            // GHOST CALL PROTECTION:
            // Si el backend dice que estamos DISPONIBLES/OFFLINE/BREAK, pero sigue enviando una active_call (ghost),
            // debemos ignorarla para limpiar la UI, A MENOS que sea una llamada entrante (Ringing) legítima.
            const isFreeStatus = ['available', 'offline', 'break'].includes(backendStatus) || (backendStatus && backendStatus.startsWith('break_'));

            if (activeCallData && isFreeStatus) {
                // Solo permitimos pasar la llamada si es INBOUND y está en estado RINGING/INCALL (casos raros de race condition)
                // Si es OUTBOUND y estamos AVAILABLE, es definitivamente una ghost call de la anterior.
                // UNLESS we just manually loaded a contact and are about to dial? No, active_call comes from backend.
                // If we are dialing, isDialing is true and we skip sync.

                // [REVERTED] Race Condition Protection was triggering prematurely on new calls.
                // We rely on handleSoftphoneCallEnded to set isWrappingUp(true) to allow the "Force Wrapup" check above to work.

                if (activeCallData.direction === 'outbound') {
                    console.warn('[Sync] Ghost Outbound Call detected while Free - Ignoring', activeCallData);
                    activeCallData = null;
                } else if (activeCallData.status !== 'ringing') {
                    // Si es Inbound pero NO está timbrando y estamos libres -> Ghost
                    // (Unless it has a form, handled above)
                    if (backendStatus === 'available') {
                        console.warn('[Sync] Ghost Inbound Call detected while Available - Ignoring', activeCallData);
                        activeCallData = null;
                    }
                }
            }

            if (activeCallData) {
                // Comprobar si es una nueva llamada para reiniciar estados locales específicos
                // [FIX] NEW CALL DETECTION BUG:
                // Before: "activeCallIdRef.current && activeCallIdRef.current !== activeCallData.call_id"
                // This caused the reset to BE SKIPPED if we started from 'activeCallIdRef.current = null' (Available).
                // Change: Check if IDs are different (null vs 123 is different).
                if (activeCallIdRef.current !== activeCallData.call_id) {
                    console.log("[Sync] New Call Detected - Resetting Form State", activeCallData.call_id);
                    selectorDismissedRef.current = false;

                    // [NUEVO] CRÍTICO: Si cambia el ID de llamada, el formulario anterior ya no es válido
                    // Esto asegura que "Actualizar Gestión" desaparezca y vuelva a "Guardar"
                    setFormSaved(false);
                    // Resetear formulario visualmente para CUALQUIER nueva llamada detectada (Saliente o Entrante)
                    // Si el usuario cargó datos manualmente, resetSessionState ya corrió antes de esto.
                    // Si no lo hizo, esto es una red de seguridad.
                    form.resetFields();
                }
                activeCallIdRef.current = activeCallData.call_id;

                // Call started, so manual load protection is no longer needed (we have a real call now)
                isManualContactLoadedRef.current = false;

                setIsInCall(backendStatus !== 'wrapup'); // [FIX] Evitar que wrapup se sobrescriba falsamente a incall
                setIsDialing(false);
                isInboundRef.current = activeCallData.direction === 'inbound';
                // Temporizador manejado por efecto de marca de tiempo ahora
                setCurrentCall(activeCallData);

                // [FIX] SNAPSHOT HYDRATION:
                // If we have form_data from server (and local form is empty/unsaved), hydrate it!
                // This covers page reloads or device switching during active calls.
                if (activeCallData.form_data && activeCallData.form_data !== '') {
                    // Check if we need to hydrate
                    // 1. Is form local state empty? (Simple check: formSaved defaults to false on reload)
                    // 2. Or if formSaved is false but we have data in server -> Force load?
                    // Safer: If !formSaved, try to load. Antd form handles setFieldsValue merging or overwriting.

                    if (!formSaved) {
                        try {
                            const serverFormData = typeof activeCallData.form_data === 'string'
                                ? JSON.parse(activeCallData.form_data)
                                : activeCallData.form_data;

                            console.log("[Hydration] Hydrating form data from server:", serverFormData);
                            form.setFieldsValue(serverFormData);
                            setFormSaved(true); // Mark as saved since it comes from DB
                        } catch (e) {
                            console.error("[Hydration] Error parsing form data", e);
                        }
                    }
                }

                // [NUEVO] Sincronizar estados de persistencia (Refactor)
                if (data.campaign_contact) {
                    setCurrentCampaignContactId(data.campaign_contact.id);
                } else if (activeCallData.campaign_contact_id) {
                    setCurrentCampaignContactId(activeCallData.campaign_contact_id);
                }

                if (activeCallData.form_id) {
                    setHasPendingForm(true);
                }

                // ACTUALIZAR CAMPAÑA DESDE LLAMADA
                if (activeCallData.campaign_id && campaigns.length > 0) {
                    const callCampaign = campaigns.find(c => c.id === parseInt(activeCallData.campaign_id));
                    // [FIX] Architecture: Only auto-select outbound campaigns to protect ManualOutboundWidget
                    if (callCampaign && (!selectedCampaign || selectedCampaign.id !== callCampaign.id) && callCampaign.type === 'outbound') {
                        setSelectedCampaign(callCampaign);
                    }
                }

                // 4. Contacto / Participante
                if (data.participant) {
                    setCurrentContact(prev => {
                        // [FIX] If we have a Campaign Contact (Prospect), NEST the incoming participant to preserve metadata
                        if (prev && prev.is_campaign_contact && !prev.uuid) {
                            return {
                                ...prev,
                                participant: {
                                    ...data.participant,
                                    phones: data.participant.phones || []
                                },
                                participant_id: data.participant.id,
                                is_identified: true
                            };
                        }
                        // Otherwise, use the participant as the main contact (Inbound / Direct)
                        return {
                            ...data.participant,
                            phones: data.participant.phones || []
                        };
                    });
                    setHasContact(true);

                    // CORRECCIÓN DE PERSISTENCIA: No cerrar formulario de creación si el usuario está activo, a menos que estemos seguros (ej. guardado)
                    // USE REFS to avoid race conditions
                    if (!isCreatingParticipantRef.current && !isAssociatingParticipantRef.current) {
                        setShowAddParticipant(false);
                    }

                    setCurrentForm(data.form);
                    setCurrentCallId(activeCallData.call_id);

                    // Cerrar selector si estaba abierto (estado resuelto)
                    setIsSelectorModalVisible(false);
                } else if (data.found_participants && data.found_participants.length > 0) {
                    // NUEVO: Manejar múltiples candidatos (Ambigüedad) - FIELD CORREGIDO: found_participants
                    setParticipantCandidates(data.found_participants);

                    // No tenemos un contacto "fijo" aún
                    setHasContact(false);
                    setCurrentContact(null);

                    // Mostrar selector si no fue descartado manualmente por el usuario en esta sesión de llamada
                    if (!selectorDismissedRef.current) {
                        setIsSelectorModalVisible(true);
                        setShowAddParticipant(false);
                    }

                    if (activeCallData) {
                        setCurrentForm(data.form);
                        setCurrentCallId(activeCallData.call_id);
                    }
                } else if (data.campaign_contact) {
                    // NUEVO: Manejar contacto de campaña (Metadatos)

                    const serverContactId = data.campaign_contact.id;
                    const localContactId = currentContact?.id;
                    const hasActiveCall = !!activeCallData;

                    if (localContactId && localContactId !== serverContactId && hasActiveCall) {
                        console.log(`[Sync] Switching contact from ${localContactId} to ${serverContactId} (Server prioritized matching)`);
                    }
                    const meta = data.campaign_contact.metadata || {};
                    let contactName = 'Prospecto';
                    Object.keys(meta).forEach(k => {
                        if (/nombre|name/i.test(k)) contactName = meta[k];
                    });

                    setCurrentContact({
                        id: data.campaign_contact.id,
                        campaign_contact_id: data.campaign_contact.id, // NUEVO: Para consistencia
                        name: contactName,
                        phones: [data.campaign_contact.phone],
                        metadata: meta,
                        is_campaign_contact: true,
                        campaign_id: data.campaign_contact.campaign_id
                    });
                    setCurrentCampaignContactId(data.campaign_contact.id); // Asegurar persistencia del ID
                    setHasContact(true);

                    if (activeCallData) {
                        setCurrentForm(data.form);
                        setCurrentCallId(activeCallData.call_id);
                    }
                    // Only close selector if we don't have candidates to choose from
                    if (!participantCandidates || participantCandidates.length === 0) {
                        setIsSelectorModalVisible(false);
                    }
                } else {
                    // Lógica de preservación de salientes Y Contactos Manuales (Reagendados)
                    const isOutbound = activeCallData?.direction === 'outbound';

                    // [FIX] Protegemos el contacto si fue cargado manualmente (e.g. Reagendado) y tiene campaign_contact_id
                    // incluso si el snapshot no lo devuelve.
                    const isManualCampaignContact = currentContact?.is_campaign_contact && currentContact?.campaign_contact_id;

                    if (isOutbound || isManualCampaignContact) {
                        if (!hasContact || !currentContact) {
                            setCurrentContact(null);
                            setHasContact(false);
                            setShowAddParticipant(false);
                            setParticipantCandidates([]);
                            setIsSelectorModalVisible(false);
                        }
                    } else {
                        setCurrentContact(null);
                        setHasContact(false);
                    }
                    setCurrentForm(data.form);
                }
            } else {
                // Sin llamada activa
                setIsInCall(false);

                // Sticky Call ID Logic: Only clear if truly free
                // FIX: Use backendStatus local variable instead of status state to avoid closure staleness
                const cleanStatus = backendStatus || 'online';
                if (['available', 'offline', 'break'].includes(cleanStatus) || cleanStatus.startsWith('break_')) {
                    activeCallIdRef.current = null;

                    // Also clear participant info when becoming available
                    // PROTECCION: No limpiar si acabamos de cargar un contacto manual para llamar
                    if (!isManualContactLoadedRef.current) {
                        setCurrentContact(null);
                        setHasContact(false);
                        setShowAddParticipant(false);
                        setParticipantCandidates([]);
                        setIsSelectorModalVisible(false); // Ensure selector is closed ONLY if not manual load
                    }

                    // FIX: Clear Call and Form state to prevent stale UI
                    setCurrentCall(null);
                    setCurrentForm(null); // [FIX] Must be null, not {}, to avoid fields.map crash
                    setIsCreatingParticipant(false);
                    isCreatingParticipantRef.current = false;
                    setIsAssociatingParticipant(false);
                    isAssociatingParticipantRef.current = false;
                }

                // CORRECCIÓN: Restablecer campaña a saliente si se quedó en entrante
                if (selectedCampaign && selectedCampaign.type === 'inbound') {
                    const firstOutbound = campaigns.find(c => c.type === 'outbound');
                    if (firstOutbound) setSelectedCampaign(firstOutbound);
                }

                // Solo limpiar formulario si NO estamos en cierre (esperando formulario)
                if (status !== 'wrapup' && !savedDraft) {
                    // Comprobar savedDraft para evitar limpiar al refrescar antes de recuperar
                    // Pero ya comprobamos la recuperación arriba.
                }

                // Si estábamos en llamada/timbrando y ahora no...
                // Manejado principalmente por eventos del softphone, pero sync lo confirma.
            }
        } catch (error) {
            console.error('Error syncing state:', error);
        }
    }, [
        isDialing, campaigns, selectedCampaign, currentContact, hasContact,
        isCreatingParticipant, isAssociatingParticipant, status, form,
        isWrappingUp, // [CRITICAL FIX] Avoid stale closure
        // Dependencias que son refs mutables no necesitan estar aquí pero es más seguro para el linter
    ]);

    // [OPTIMIZATION] Use ref to hold the latest sync function without triggering interval reset
    const syncRef = useRef(syncWithServer);

    // Update ref when syncWithServer changes (which is often, due to dependencies)
    React.useEffect(() => {
        syncRef.current = syncWithServer;
    }, [syncWithServer]);

    // Single interval setup on mount
    React.useEffect(() => {
        // Initial sync
        if (syncRef.current) syncRef.current();

        const interval = setInterval(() => {
            if (syncRef.current) syncRef.current();
        }, 5000); // Increased to 5000ms to reduce load

        return () => clearInterval(interval);
    }, []); // Empty dependency array = run once on mount

    const getValidCallId = () => {
        return currentCallId || activeCallIdRef.current || (currentCall ? currentCall.call_id : null);
    };

    // Actualizar valor del contexto con todos los estados y manejadores
    const value = {
        // State
        activeTab, setActiveTab,
        isSelectorModalVisible, setIsSelectorModalVisible,
        isRescheduleModalVisible, setIsRescheduleModalVisible,
        isAddCardModalVisible, setIsAddCardModalVisible,
        status, setStatus,
        isInCall, setIsInCall,
        isDialing, setIsDialing,
        formSaved, setFormSaved,
        callSeconds, setCallSeconds,
        stateSeconds, setStateSeconds,
        breakSeconds, setBreakSeconds,
        breakStatuses, setBreakStatuses,
        hasContact, setHasContact,
        isLoadingContact, setIsLoadingContact,
        currentContact, setCurrentContact,
        participantCandidates, setParticipantCandidates,
        isLoadingHistory, setIsLoadingHistory,
        recentCalls, setRecentCalls,
        currentForm, setCurrentForm,
        selectedPhone, setSelectedPhone,
        showAddParticipant, setShowAddParticipant,
        isCreatingParticipant, setIsCreatingParticipant,
        isAssociatingParticipant, setIsAssociatingParticipant,
        isSavingParticipant, setIsSavingParticipant,
        campaigns, setCampaigns,
        isLoadingCampaigns, setIsLoadingCampaigns,
        selectedCampaign, setSelectedCampaign,
        rescheduledCalls, setRescheduledCalls,
        isLoadingRescheduled, setIsLoadingRescheduled,
        isGettingNext, setIsGettingNext,
        rescheduleDate, setRescheduleDate,
        isRescheduling, setIsRescheduling,
        rescheduleCampaignFilter, setRescheduleCampaignFilter,
        currentCallId, setCurrentCallId,
        currentCall, setCurrentCall,
        availableTags, setAvailableTags,
        availableGroups, setAvailableGroups,
        monitoredCalls, setMonitoredCalls,
        monitoredTimers, setMonitoredTimers,

        // Persistencia (Refactor)
        currentCampaignContactId, setCurrentCampaignContactId,
        hasPendingForm, setHasPendingForm,
        isRescheduled, setIsRescheduled,
        rescheduleData, setRescheduleData,

        // Forms
        form,
        addParticipantForm,
        associationForm,
        addCardForm,

        // Refs
        selectorDismissedRef,
        activeCallIdRef,
        isInboundRef,
        isRescheduledSession,
        callTimerRef,
        stateTimerRef,
        breakTimerRef,

        // Timestamps & Sync
        getValidCallId,
        timestamps, setTimestamps,
        syncWithServer,
        handleFormChange,
        clearDraft,

        // Acciones de Participantes
        fetchParticipants,
        fetchUbigeo,
        handleStartCreatingParticipant,
        handleCancelCreatingParticipant,
        handleStartAssociatingParticipant,
        handleCancelAssociatingParticipant,
        handleAssociateParticipant,
        handleSaveParticipant,
        handleSelectParticipant,
        handleCloseSelector,
        handleOpenAddCardModal,
        handleCloseAddCardModal,
        handleSaveCard,

        // Acciones de Campaña
        hasOutbound,
        resetSessionState, // [NUEVO]
        isWrappingUp, setIsWrappingUp, // [NUEVO]
        handleNextContact,
        handleReleaseContact,
        resetInteraction: () => {
            setCurrentContact(null);
            setHasContact(false);
            setCurrentCallId(null);
            setCurrentCall(null);
            setCurrentCampaignContactId(null);
            setHasPendingForm(false);
            setIsRescheduled(false);
            setRescheduleData(null);
            isRescheduledSession.current = false;
            setIsWrappingUp(false); // [NUEVO]
            form.resetFields();
            setFormSaved(false);
        },
        setManualContactLoaded: (val) => { isManualContactLoadedRef.current = val; },

        // [EVENT-DRIVEN] FAST PATH UI UPDATES
        handleSoftphoneEvent: React.useCallback((eventType) => {
            console.log(`[EventBridge] Fast UI Update triggered: ${eventType}`);
            if (eventType === 'connected') {
                setIsInCall(true);
                setIsDialing(false);
                setFormSaved(false); // Safety reset for new forms
                setIsWrappingUp(false);
                syncWithServer(); // Immediate background sync
            } else if (eventType === 'ringing') {
                setIsInCall(false);
                setIsDialing(true);
                // We might not know direction yet via event, sync will catch up
            }
            // Note: 'terminated' is handled locally in CallCenterPage via the window event to easily access currentForm state
        }, [syncWithServer, setIsInCall, setIsDialing, setFormSaved, setIsWrappingUp])
    };

    return (
        <CallCenterContext.Provider value={value}>
            {children}
        </CallCenterContext.Provider>
    );
};
